import { useState, useEffect } from 'react';
import { Select, Button, Space, Spin, message } from 'antd';
import { EnvironmentOutlined, LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useLocationStore } from '@/stores/location.store';
import { apiService } from '@/services/api.service';
import { City, ReverseGeocodeResponse } from '@rescue-foodies/shared-types';

/**
 * LocationSelector component
 * Allows users to select their location via dropdown or geolocation
 */
export const LocationSelector = () => {
  const { selectedCity, setLocation, isLoading, setLoading } = useLocationStore();
  const [localLoading, setLocalLoading] = useState(false);

  // Fetch supported cities from API
  const { data: cities, isLoading: citiesLoading } = useQuery<City[]>({
    queryKey: ['locations', 'cities'],
    queryFn: () => apiService.get<City[]>('/locations/cities'),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour (cities don't change often)
  });

  /**
   * Handle manual city selection from dropdown
   */
  const handleCitySelect = (cityName: string) => {
    const city = cities?.find((c) => c.name === cityName);
    if (city) {
      setLocation(city.name, city.lat, city.lng);
      message.success(`Location set to ${city.name}`);
    }
  };

  /**
   * Handle "Use My Location" button click
   * Requests browser geolocation and reverse geocodes to city
   */
  const handleUseMyLocation = async () => {
    if (!navigator.geolocation) {
      message.error('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    setLocalLoading(true);

    try {
      // Get user's current position
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          enableHighAccuracy: false,
          maximumAge: 300000, // Accept cached location up to 5 minutes old
        });
      });

      const { latitude, longitude } = position.coords;

      // Call reverse geocode API
      const result = await apiService.get<ReverseGeocodeResponse>(
        '/locations/reverse-geocode',
        {
          params: { lat: latitude, lng: longitude },
        }
      );

      setLocation(result.city, result.coordinates.lat, result.coordinates.lng);
      message.success(`Location set to ${result.city}`);
    } catch (error: any) {
      console.error('Geolocation error:', error);

      // Handle specific geolocation errors
      if (error.code === 1) {
        // PERMISSION_DENIED
        message.error('Please select your city manually or enable location access');
      } else if (error.code === 2) {
        // POSITION_UNAVAILABLE
        message.error('Location unavailable. Please select your city manually');
      } else if (error.code === 3) {
        // TIMEOUT
        message.error('Location request timed out. Please select your city manually');
      } else {
        message.error('Could not detect your location. Please select manually');
      }
    } finally {
      setLoading(false);
      setLocalLoading(false);
    }
  };

  const isComponentLoading = isLoading || localLoading || citiesLoading;

  return (
    <Space.Compact style={{ width: '100%', maxWidth: 320 }}>
      {/* City dropdown */}
      <Select
        value={selectedCity || undefined}
        style={{ minWidth: 180 }}
        onChange={handleCitySelect}
        placeholder="Select City"
        suffixIcon={<EnvironmentOutlined />}
        loading={citiesLoading}
        disabled={isComponentLoading}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        options={cities?.map((city) => ({
          value: city.name,
          label: city.name,
        }))}
      />

      {/* Use My Location button */}
      <Button
        icon={
          isComponentLoading ? (
            <Spin indicator={<LoadingOutlined spin />} size="small" />
          ) : (
            <EnvironmentOutlined />
          )
        }
        onClick={handleUseMyLocation}
        disabled={isComponentLoading}
        title="Use My Location"
      />
    </Space.Compact>
  );
};
